import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
export interface CartLine {
  /** Unique key combining the product id and the selected option. */
  key: string;
  id: string;
  optionId?: string;
  quantity: number;
}
export interface SavedLine {
  key: string;
  id: string;
  optionId?: string;
}
interface CartContextValue {
  lines: CartLine[];
  saved: SavedLine[];
  totalItems: number;
  addItem: (id: string, optionId?: string, quantity?: number) => void;
  setQuantity: (key: string, quantity: number) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  saveForLater: (id: string, optionId?: string) => void;
  removeSaved: (key: string) => void;
  isSaved: (id: string, optionId?: string) => boolean;
}
export function lineKey(id: string, optionId?: string): string {
  return optionId ? `${id}::${optionId}` : id;
}
const CartContext = createContext<CartContextValue | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [saved, setSaved] = useState<SavedLine[]>([]);
  const addItem = useCallback((id: string, optionId?: string, quantity = 1) => {
    const key = lineKey(id, optionId);
    setLines(current => {
      const existing = current.find(line => line.key === key);
      if (existing) {
        return current.map(line => line.key === key ? {
          ...line,
          quantity: line.quantity + quantity
        } : line);
      }
      return [...current, {
        key,
        id,
        optionId,
        quantity
      }];
    });
  }, []);
  const setQuantity = useCallback((key: string, quantity: number) => {
    setLines(current => quantity <= 0 ? current.filter(line => line.key !== key) : current.map(line => line.key === key ? {
      ...line,
      quantity
    } : line));
  }, []);
  const removeItem = useCallback((key: string) => {
    setLines(current => current.filter(line => line.key !== key));
  }, []);
  const clear = useCallback(() => setLines([]), []);
  const saveForLater = useCallback((id: string, optionId?: string) => {
    const key = lineKey(id, optionId);
    setSaved(current => current.some(line => line.key === key) ? current.filter(line => line.key !== key) : [...current, {
      key,
      id,
      optionId
    }]);
  }, []);
  const removeSaved = useCallback((key: string) => {
    setSaved(current => current.filter(line => line.key !== key));
  }, []);
  const value = useMemo(() => {
    const totalItems = lines.reduce((sum, line) => sum + line.quantity, 0);
    const isSaved = (id: string, optionId?: string) => saved.some(line => line.key === lineKey(id, optionId));
    return {
      lines,
      saved,
      totalItems,
      addItem,
      setQuantity,
      removeItem,
      clear,
      saveForLater,
      removeSaved,
      isSaved
    };
  }, [lines, saved, addItem, setQuantity, removeItem, clear, saveForLater, removeSaved]);
  return <CartContext.Provider value={value} data-id="element-296" data-mp="2jd769znm2tok">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}